import React, { ChangeEvent, useState } from 'react';

import { Radio, RadioGroup, TitleWithDescription } from '@infoods/lifebase-ui';

import { LbModal } from '../../../../shared/components/modals';
import { SelectableCard } from '../../../../shared/components/form-components';
import { REPORT_TYPE, ReportType } from './GenerateReportModal.settings';
import classes from './GenerateReportModal.module.scss';
import { InbodyResultSheetModal } from '../InbodyResultSheetModal/InbodyResultSheetModal';
import { ProgressReportModal } from '../ProgressReportModal/ProgressReportModal';
import { CUSTOMER_TYPE } from '../../../../shared/constants/customer/customerType';
import { openModal } from '../../../../shared/utils/reactUtils/openModal/openModal';

type GenerateReportModalProps = {
  customerId: number;
  entityId: number;
  customerName: { firstName: string | null; lastName: string | null };
  onClose: () => void;
};

const reportTypeCards = [
  {
    value: REPORT_TYPE.RESULT_SHEET,
    title: 'InBody Result Sheet',
    description: 'Detailed body composition measurements from an InBody scan.',
  },
  {
    value: REPORT_TYPE.PROGRESS_REPORT,
    title: 'Progress Report',
    description:
      "An easy-to-read summary of your client's progress, trends, and areas to focus on.",
  },
];
// support only for scan iq customers now
const GenerateReportModal = ({
  customerId,
  entityId,
  customerName,
  onClose,
}: GenerateReportModalProps) => {
  const [selectedType, setSelectedType] = useState<ReportType>(REPORT_TYPE.RESULT_SHEET);

  const handleSelectType = (event: ChangeEvent<HTMLInputElement>, value: string) => {
    setSelectedType(value as ReportType);
  };

  const openResultSheetModal = () => {
    openModal((closeModal) => (
      <InbodyResultSheetModal
        customerType={CUSTOMER_TYPE.SCAN_IQ}
        customerId={customerId}
        entityId={entityId}
        customerName={customerName}
        onClose={closeModal}
      />
    ));
  };

  const openProgressReportModal = () => {
    openModal((closeModal) => (
      <ProgressReportModal
        customerId={customerId}
        entityId={entityId}
        onClose={closeModal}
      />
    ));
  };

  const handleConfirm = () => {
    onClose();

    switch (selectedType) {
      case REPORT_TYPE.RESULT_SHEET:
        openResultSheetModal();
        break;
      case REPORT_TYPE.PROGRESS_REPORT:
        openProgressReportModal();
        break;
      default:
        throw new Error(`Invalid report type: ${selectedType}`);
    }
  };

  return (
    <LbModal
      open
      title="Generate Report"
      onClose={onClose}
      primaryBtnConfig={{ onClick: handleConfirm, text: 'Generate and Download' }}
      secondaryBtnConfig={{ onClick: onClose, text: 'Cancel' }}
    >
      <RadioGroup
        className={classes.radioGroup}
        value={selectedType}
        onChange={handleSelectType}
      >
        {reportTypeCards.map(({ value, title, description }) => (
          <SelectableCard key={value} variant="inBox" className={classes.card}>
            <Radio value={value} className={classes.radio} />
            <TitleWithDescription
              title={title}
              description={description}
              titleFont="title-16-bold"
            />
          </SelectableCard>
        ))}
      </RadioGroup>
    </LbModal>
  );
};

export { GenerateReportModal };
